import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Col, Container, Row, Table } from 'reactstrap';
import { Breadcrumbs, ScreenOverlay, Spinner } from '../../components/Common';
import useAuthUser from '../../common/hooks/authUser';
import {
	dealerInventorySearchPath,
	isDealerPortalNavFromInventory,
} from '../../common/dealerPortalNavigation';
import type { TDealerPortalNavState } from '../../common/dealerPortalNavigation';
import { toastError } from '../../helpers/toast-helper';
import { getFormattedCurrency } from '../../helpers/currency-helper';
import { getTableFormattedDate } from '../../helpers/date-time-helper';
import { normalizeCustomerNumberForLookup } from '../../helpers/dealer-customer-input';
import { getDealerPortalOrders } from '../../api/dealerPortal';
import type { TSyteLineOrder } from '../../api/dealerPortal/types';

function formatOrderDate(value: unknown): string {
	if (value == null || value === '') return '—';
	const s = typeof value === 'string' ? value : String(value);
	return getTableFormattedDate(s) || '—';
}

const DealerOrdersPage = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const [searchParams] = useSearchParams();
	const { user, isAdmin, isDataLoading } = useAuthUser();
	const [orders, setOrders] = useState<TSyteLineOrder[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const redirectDoneRef = useRef(false);

	const customerNumber = useMemo(() => {
		const raw = searchParams.get('customerNumber') ?? '';
		return normalizeCustomerNumberForLookup(raw);
	}, [searchParams]);

	useEffect(() => {
		document.title = 'Orders';
	}, []);

	useEffect(() => {
		if (user && !isAdmin) {
			toastError('Access denied. Admin privileges required.');
			navigate('/');
		}
	}, [user, isAdmin, navigate]);

	const loadOrders = useCallback(async () => {
		if (!customerNumber) return;
		setIsLoading(true);
		setOrders([]);
		try {
			const list = await getDealerPortalOrders(customerNumber);
			const arr = Array.isArray(list) ? list : [];
			const filtered = arr.filter((o) => o.orderNumber);
			if (arr.length > 0 && filtered.length === 0 && arr[0]?.errorMessage) {
				toastError(arr[0].errorMessage);
			}
			setOrders(filtered);
		} catch (e) {
			toastError(e instanceof Error ? e.message : 'Failed to load orders.');
		} finally {
			setIsLoading(false);
		}
	}, [customerNumber]);

	useEffect(() => {
		if (!user || !isAdmin) return;
		const allowed = isDealerPortalNavFromInventory(location.state);
		if (!allowed || !customerNumber) {
			if (!redirectDoneRef.current) {
				redirectDoneRef.current = true;
				toastError('Open orders from Dealer inventory after selecting a dealer.');
				navigate('/admin/dealer-inventory', { replace: true });
			}
			return;
		}
		void loadOrders();
	}, [user, isAdmin, location.state, customerNumber, navigate, loadOrders]);

	if (isDataLoading) {
		return <Spinner />;
	}

	if (user && !isAdmin) {
		return null;
	}

	if (!isDealerPortalNavFromInventory(location.state) || !customerNumber) {
		return null;
	}

	const navState = { fromDealerInventory: true } satisfies TDealerPortalNavState;

	return (
		<Container fluid>
			<Breadcrumbs title="Orders" />
			<Row className="mb-3">
				<Col>
					<Button
						tag={Link}
						to={dealerInventorySearchPath(customerNumber)}
						color="light"
						className="me-2 mb-2"
						type="button"
					>
						← Dealer inventory
					</Button>
				</Col>
			</Row>
			<Row className="mb-3">
				<Col md={8}>
					<p className="mb-0 text-muted small">Customer number</p>
					<p className="mb-0 fw-semibold">{customerNumber}</p>
				</Col>
			</Row>

			<div
				className="position-relative"
				style={isLoading ? { minHeight: '12rem' } : undefined}
			>
				{isLoading && (
					<>
						<ScreenOverlay />
						<Spinner />
					</>
				)}
				<div className="table-responsive">
					<Table bordered hover size="sm" className="align-middle">
						<thead className="table-light">
							<tr>
								<th>Order date</th>
								<th>Order #</th>
								<th>Customer PO</th>
								<th className="text-end">Total amount</th>
								<th style={{ width: 80 }} />
							</tr>
						</thead>
						<tbody>
							{!isLoading && orders.length === 0 && (
								<tr>
									<td colSpan={5} className="text-muted">
										No orders returned.
									</td>
								</tr>
							)}
							{orders.map((o, idx) => {
								const on = o.orderNumber ?? '';
								const detailTo = `/admin/orders/${encodeURIComponent(on)}?customerNumber=${encodeURIComponent(customerNumber)}`;
								return (
									<tr key={`${on}-${idx}`}>
										<td>{formatOrderDate(o.orderDate)}</td>
										<td>{on || '—'}</td>
										<td>{o.customerPO ?? '—'}</td>
										<td className="text-end">{getFormattedCurrency(o.orderCost)}</td>
										<td>
											{on ? (
												<Link to={detailTo} state={navState}>
													view
												</Link>
											) : (
												<span className="text-muted">—</span>
											)}
										</td>
									</tr>
								);
							})}
						</tbody>
					</Table>
				</div>
			</div>
		</Container>
	);
};

export default DealerOrdersPage;
